/**
 * Subscription and Usage Tracking Types
 * LessonlyAI - Usage Tracking System
 */

// Subscription Tiers
export type SubscriptionTier = 'free' | 'essential' | 'pro';

// Subscription Status
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing';

// Tier Configuration
export interface TierConfig {
  tier: SubscriptionTier;
  name: string;
  monthlyLimit: number;
  price: number | null; // null for free
  priceId?: string; // Stripe price ID
  features: string[];
}

// Subscription record from database
export interface Subscription {
  id: string;
  user_id: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  trial_end: string | null;
  created_at: string;
  updated_at: string;
}

// Usage tracking record from database
export interface UsageTracking {
  id: string;
  user_id: string;
  month_year: string; // Format: "2025-01"
  lessons_generated: number;
  last_reset_at: string;
  created_at: string;
  updated_at: string;
}

// Combined usage statistics
export interface UsageStats {
  used: number;
  limit: number;
  remaining: number;
  tier: SubscriptionTier;
  monthYear: string;
  percentage: number;
  canGenerate: boolean;
}

// User subscription details (from RPC function or view)
export interface UserSubscriptionDetails {
  user_id: string;
  subscription_id?: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  trial_end: string | null;
  lessons_generated: number;
  month_year: string;
  monthly_limit: number;
  lessons_remaining: number;
  lessons_used_this_month?: number; // Alternative field name
}

// Tier configurations
export const TIER_CONFIGS: Record<SubscriptionTier, TierConfig> = {
  free: {
    tier: 'free',
    name: 'Free',
    monthlyLimit: 5,
    price: null,
    features: [
      '5 lesson plans per month',
      'Basic templates',
      'TEKS alignment',
      'Community support',
    ],
  },
  essential: {
    tier: 'essential',
    name: 'Essential',
    monthlyLimit: 60,
    price: 7.99,
    priceId: import.meta.env.VITE_STRIPE_ESSENTIAL_MONTHLY_PRICE_ID,
    features: [
      '60 AI lesson plans per month',
      'Save unlimited lessons',
      'Spanish lesson plans',
      'All differentiation modes',
      'TEKS-aligned content',
      'PDF export',
    ],
  },
  pro: {
    tier: 'pro',
    name: 'Pro',
    monthlyLimit: 200,
    price: 12.99,
    priceId: import.meta.env.VITE_STRIPE_PRO_MONTHLY_PRICE_ID,
    features: [
      '200 AI lesson plans per month',
      'Everything in Essential, plus:',
      'Google Drive export (coming soon)',
      'Priority email support',
      'Early access to new features',
    ],
  },
};

// Helper functions
export function getTierConfig(tier: SubscriptionTier): TierConfig {
  return TIER_CONFIGS[tier];
}

export function getTierLimit(tier: SubscriptionTier): number {
  return TIER_CONFIGS[tier].monthlyLimit;
}

export function isPaidTier(tier: SubscriptionTier): boolean {
  return tier !== 'free';
}

export function isUnlimitedTier(tier: SubscriptionTier): boolean {
  return tier === 'pro';
}

/**
 * Normalize tier strings for backwards compatibility during migration
 * Maps old tier names (essential, pro) to new names (teacher, unlimited)
 * @param tier - Tier string from database (may be old or new format)
 * @returns Normalized tier matching SubscriptionTier type
 */
export function normalizeTier(tier: string): SubscriptionTier {
  if (tier === 'essential') return 'essential';
  if (tier === 'pro') return 'pro';
  if (tier === 'free') return 'free';
  // Backwards compat: old DB records may have previous tier names
  if (tier === 'teacher') return 'essential';
  if (tier === 'unlimited') return 'pro';
  // Default safe fallback
  console.warn(`Unknown tier "${tier}", defaulting to free`);
  return 'free';
}

// Get current month in YYYY-MM format
export function getCurrentMonthYear(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

// Get next month reset date
export function getNextResetDate(): Date {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return nextMonth;
}

// Format reset date for display
export function formatResetDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  });
}

// Calculate usage percentage
export function calculateUsagePercentage(used: number, limit: number): number {
  if (limit === 0) return 0;
  if (limit >= 999999) return 0; // Unlimited
  return Math.min(100, Math.round((used / limit) * 100));
}

// Get progress bar color based on percentage
export function getProgressBarColor(percentage: number): string {
  if (percentage <= 50) return 'green';
  if (percentage <= 80) return 'yellow';
  return 'red';
}

// Check if user is at or near limit
export function isNearLimit(remaining: number, limit: number): boolean {
  if (limit >= 999999) return false; // Unlimited
  const threshold = Math.max(2, Math.ceil(limit * 0.2)); // 20% or 2 lessons
  return remaining <= threshold;
}

export function isAtLimit(remaining: number): boolean {
  return remaining <= 0;
}

// Error types
export class UsageLimitError extends Error {
  constructor(tier: SubscriptionTier, limit: number) {
    super(`Monthly lesson limit reached. Upgrade to generate more lessons.`);
    this.name = 'UsageLimitError';
  }
}

export class SubscriptionNotFoundError extends Error {
  constructor(userId: string) {
    super(`Subscription not found for user ${userId}`);
    this.name = 'SubscriptionNotFoundError';
  }
}
